import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, TextField, Stack, Grid, Typography, Divider } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ReCAPTCHA from 'react-google-recaptcha';
import validator from 'validator';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useGetOemTerms, validateNIP } from 'api';
import { RegisterInvitationUser, ValidateCaptcha, RegisterUser } from 'engine/baseApiClient';
import { doLogInAsync } from 'features/logon/logonSlice';
import navigationPaths from 'engine/navigationPaths';
import { theme } from 'oems/theme';
import { resources } from 'oems/resources';

import { Button, Checkbox } from 'components/ui';
import { config } from 'oems/config';

const schema = Yup.object({
  nip: Yup.string().trim().required('Pole wymagane'),
  email: Yup.string().trim().email('Niepoprawny email').required('Pole wymagane'),
  password: Yup.string().trim().required('Pole wymagane'),
  passwordConfirmation: Yup.string().when('password', ([password], field) =>
    password
      ? field.required('Blednie powtórzone haslo').oneOf([Yup.ref('password')], 'Blednie powtórzone haslo')
      : field,
  ),
  captchaPassed: Yup.bool(),
});

export default function RegistrationForm(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const captchaRef = useRef(null);
  const userContext = props?.onGetUserContext !== undefined ? props.onGetUserContext() : '';

  const [showPassword, setShowPassword] = React.useState(false);

  const { data: termsData, isLoading: isTermsDataLoading } = useGetOemTerms();

  // #region OEM password validation

  const isStrongPassword = (password) => {
    return validator.isStrongPassword(password, {
      minLength: config.password.minLength, 
      minLowercase: config.password.minLowercase, 
      minUppercase: config.password.minUppercase, 
      minNumbers: config.password.minNumbers, 
      minSymbols: config.password.minSymbols
    });
  }

  const getPasswordComplexityHelperText = config.password.complexityHelperText;

  // #endregion

  // TODO - do implementacji
  const invitationToken = '';

  const { values, setFieldValue, errors, handleSubmit, isSubmitting, setFieldError } = useFormik({
    initialValues: {
      nip: userContext,
      email: '',
      password: '',
      passwordConfirmation: '',
      termIds: [],
      captchaPassed: false,
    },
    validationSchema: schema,
    validate: async (values) => {
      const isNipValid = await validateNIP(schema.cast(values).nip);
      const nipError = isNipValid
        ? {}
        : {
            nip: 'Niepoprawny NIP',
          };

      const isPasswordValid = isStrongPassword(schema.cast(values).password);
      const passwordError = isPasswordValid
        ? {}
        : {
            password: 'Niepoprawne hasło',
          };

      const areTermIdsValid = terms.every((term) => !(term.isRequired && !values.termIds.includes(term.versionId)));
      const termIdsError = areTermIdsValid
        ? {}
        : {
            termIds: 'Niepoprawnie zaznaczone zgody',
          };

      const captchaError = values.captchaPassed
        ? {}
        : {
            captchaPassed: 'Musimy się upewnić, że nie jesteś robotem!',
          };

      return { ...nipError, ...passwordError, ...termIdsError, ...captchaError };
    },
    validateOnChange: false,
    onSubmit: async (values) => {
      const data = schema.cast(values);
     

      try {
        const response = !invitationToken
          ? await RegisterUser({
              password: data.password,
              email: data.email,
              terms: data.termIds,
              nip: {
                nip: data.nip,
                countryCode: 'PL',
              },
            })
          : await RegisterInvitationUser({
              password: data.password,
              token: invitationToken,
            });
          
        if (response.status === 500) {
          return setFieldError('email', 'Użytkownik już istnieje');
        }
        else if (response.status === 422) {
          return setFieldError('nip', 'Niepoprawny NIP');
        }
        else if (response.status === 400) {          
          if(response.errors.length > 0)
          {           
            if(response.errors[0].property === 'password'){
              return setFieldError('passwordConfirmation', response.errors[0].message);
            }
          }   
          else{
            return setFieldError('email', 'Błąd rejestracji');
          }       
        }
        dispatch(
          doLogInAsync({
            email: data.email,
            password: data.password,
          }),
        )
          .unwrap()
          .then(() => {
            captchaRef.current.reset();
            setFieldValue('captchaPassed', false);
            props.onSuccess && props.onSuccess();
            props.onClose && props.onClose();
            props.isStandalone && navigate(navigationPaths().Home);
          });
      } catch (e) {
        // console.log(JSON.stringify(e));
      }
    },
  });

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const checkIfHuman = async () => {
    const token = captchaRef.current.getValue();

    try {
      const result = await ValidateCaptcha(token);

      if (!result.isHuman) {
        captchaRef.current.reset();
      }

      setFieldValue('captchaPassed', result.isHuman);
    } catch (e) {
      captchaRef.current.reset();
    }
  };

  const terms = useMemo(() => termsData || [], [termsData]);

  return (
    <Grid item>
      <Box sx={{ my: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <TextField
          margin="normal"
          value={values.nip}
          required
          error={!!errors.nip}
          helperText={errors.nip}
          onChange={(event) => setFieldValue('nip', event.target.value)}
          fullWidth
          label="Twój NIP"
          inputProps={{ maxLength: 10 }}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === '-') {
              e.preventDefault();
            }
          }}
          sx={{
            mb: 0,
            '.MuiFormHelperText-root': {
              mb: 0,
            },
          }}
        />

        <TextField
          margin="normal"
          value={values.email}
          required
          error={!!errors.email}
          helperText={errors.email}
          onChange={(event) => setFieldValue('email', event.target.value)}
          fullWidth
          label="Twój adres email"
          sx={{
            mb: 0,
            '.MuiFormHelperText-root': {
              mb: 0,
            },
          }}
        />

        <TextField
          margin="normal"
          value={values.password}
          required
          error={!!errors.password}
          onChange={(event) => setFieldValue('password', event.target.value)}
          fullWidth
          helperText={getPasswordComplexityHelperText}
          label="Twoje hasło"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Pokaż hasło"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 0,
            '.MuiFormHelperText-root': {
              mb: 0,
            },
          }}
        />

        <TextField
          margin="normal"
          value={values.passwordConfirmation}
          required
          error={!!errors.passwordConfirmation}
          onChange={(event) => setFieldValue('passwordConfirmation', event.target.value)}
          helperText={errors.passwordConfirmation}
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="Powtórz hasło"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Pokaż hasło"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '.MuiFormHelperText-root': {
              mb: 0,
            },
          }}
        />

        {isTermsDataLoading && <resources.LoadingIcon style={{ marginBottom: 12 }} />}
        {terms.length > 0 && (
          <Stack sx={{ mb: 3, fontSize: 14 }} gap={1}>
            {terms.map((term) => (
              <label key={term.versionId}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Stack>
                    <Checkbox
                      onChange={(checked) =>
                        setFieldValue(
                          'termIds',
                          checked
                            ? [...values.termIds, term.versionId]
                            : values.termIds.filter((item) => item !== term.versionId),
                        )
                      }
                      checked={values.termIds.includes(term.versionId)}
                    />
                  </Stack>

                  <Stack>
                    {term.url ? (
                      <a
                        href={term.url}
                        target="_blank"
                        rel="noreferrer noopener nofollow"
                        onClick={(event) => event.stopPropagation()}
                        style={{
                          color:
                            errors.termIds && term.isRequired && !values.termIds.includes(term.versionId)
                              ? theme.palette.error.main
                              : theme.palette.primary.main,
                        }}
                      >
                        {term.displayName}
                      </a>
                    ) : (
                      <span
                        style={{
                          color:
                            errors.termIds && term.isRequired && !values.termIds.includes(term.versionId)
                              ? theme.palette.error.main
                              : theme.palette.text.main,
                        }}
                      >
                        {term.content}
                      </span>
                    )}
                  </Stack>
                </Stack>
              </label>
            ))}
          </Stack>
        )}

        <ReCAPTCHA
          id="captcha"
          sitekey={process.env.REACT_APP_SITE_KEY}
          ref={captchaRef}
          onChange={() => checkIfHuman()}
          onExpired={() => setFieldValue('captchaPassed', false)}
        />

        {errors.captchaPassed && (
          <Typography color="error.main" fontSize={12}>
            Musimy się upewnić, że nie jesteś robotem!
          </Typography>
        )}

        <Divider sx={{ mt: 4 }} />

        <Button onClick={handleSubmit} fullWidth loading={isSubmitting}>
          Zarejestruj się
        </Button>
      </Box>
    </Grid>
  );
}
